import { ReactElement, useMemo } from 'react'
import { Vector3 } from 'three'
import { Line } from '@react-three/drei'

const ARROW_HEAD_ANGLE = Math.PI * (2 - 1 / 3) // 300 degrees in radians
const ARROW_HEAD_LENGTH = 0.15

export interface CommentCalloutArrowProps {
  from: Vector3
  to: Vector3
  color: string
  id: string
  index: number
  arrowHead?: boolean
}

/**
 * Callout line + optional arrow head, matching {@link Comment3D} rendering.
 */
const CommentCalloutArrow = ({
  from,
  to,
  color,
  id,
  index,
  arrowHead = false,
}: CommentCalloutArrowProps): ReactElement => {
  const arrowHeadPoints = useMemo(() => {
    if (!arrowHead) return null

    const direction = new Vector3().subVectors(to, from).normalize()

    const up = new Vector3(0, 0, 1)
    let perpendicular = new Vector3().crossVectors(direction, up)
    if (perpendicular.length() < 0.001) {
      perpendicular = new Vector3().crossVectors(direction, new Vector3(1, 0, 0))
    }
    perpendicular.normalize()

    const rotAxis = new Vector3().crossVectors(perpendicular, direction)

    const halfAngle = ARROW_HEAD_ANGLE / 2

    const arrowDir1 = direction.clone().applyAxisAngle(rotAxis, halfAngle)
    const arrowDir2 = direction.clone().applyAxisAngle(rotAxis, -halfAngle)

    arrowDir1.multiplyScalar(ARROW_HEAD_LENGTH)
    arrowDir2.multiplyScalar(ARROW_HEAD_LENGTH)

    const arrowEnd1 = new Vector3().addVectors(to, arrowDir1)
    const arrowEnd2 = new Vector3().addVectors(to, arrowDir2)

    return [arrowEnd1, arrowEnd2]
  }, [from, to, arrowHead])

  return (
    <group key={`callout-${id}-${index}`}>
      <Line
        points={[from, to]}
        color={color}
        lineWidth={1}
        dashed
        dashSize={0.15}
        gapSize={0.075}
      />
      {arrowHead && arrowHeadPoints && (
        <>
          <Line points={[to, arrowHeadPoints[0]]} color={color} lineWidth={1} dashed={false} />
          <Line points={[to, arrowHeadPoints[1]]} color={color} lineWidth={1} dashed={false} />
        </>
      )}
    </group>
  )
}

export default CommentCalloutArrow
